import React, { useEffect, useRef, useState } from "react"
import { Tooltip } from "@mui/material"
import { useField, useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { ApiQueryAutocomplete } from "../../../../../../core/api/components/ApiQueryAutocomplete"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { BillReceiversItemMS, SelectedMainUserState } from "../../../../createOrder/createOrderTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { ConfigQuery, BillReceiversResponse, fetchBillReceiversMS } from "../../../createOrderApi"
import { getFormikFieldError } from "../../form/helpers"
import { DynamicConfigField, MeetingServiceTabValues } from "../../form/formTypes"
import { handleAutoCompleteMode, useAutocompleteController } from "../../form/useAutocompleteController"
import { useFormResetWatcher } from "../../form/useFormResetWatcher"

import { AddBillReceiverButton } from "./AddBillReceiverButton"
import { BillReceiverOptionItem } from "./BillReceiverOptionItem"
import { CreateBillReceiver } from "./CreateBillReceiver"

const useStyles = makeStyles()((theme) => ({
    buttonWrap: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.tailwind.sky[50],
    },
    noOptionsWrap: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.tailwind.sky[50],
        display: "flex",
        flexDirection: "column",
        paddingBottom: theme.spacing(0.5),
    },
    noOptionsTextWrap: {
        padding: theme.spacing(1, 1.5),
        color: theme.palette.text.secondary,
    },
    button: {
        justifyContent: "start",
        color: theme.palette.tailwind.slate[950],
        fontSize: "1rem",
        textTransform: "none",
        transition: "0.25s ease-in-out",
        width: "100%",
        padding: ".5rem 1rem",
    },
    altGrey: {
        backgroundColor: theme.palette.tailwind.slate[50],
    },
    altWhite: {
        backgroundColor: theme.palette.common.white,
    },
}))
type Props = Readonly<{
    config: DynamicConfigField
    user: SelectedMainUserState
    showCreateButton?: boolean
}>

function selectBillReceivers(data: BillReceiversResponse): BillReceiversItemMS[] {
    return data?.billReceivers ?? []
}

export function BuyerPartiesAutocomplete({ config, user, showCreateButton }: Props) {
    const { classes } = useStyles()
    const [isOpenCreateBill, setIsOpenCreateBill] = useState(false)
    const [field, meta, helpers] = useField<BillReceiversItemMS | null>(config.key)
    const { submitCount } = useFormikContext<MeetingServiceTabValues>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)
    const wasReset = useFormResetWatcher(config.key)
    const didAutoSelectSingleOptionRef = useRef(false)
    const [didInitialFetch, setDidInitialFetch] = useState(false)

    const mode = handleAutoCompleteMode(config.inputField?.mode)
    const {
        open,
        debouncedSearch,
        enabled,
        isEagerLoad,
        isLoadOnOpen,
        forceRefreshKey,
        handleInputChange,
        handleOpen,
        handleClose,
        handleClearSearch,
    } = useAutocompleteController(mode, wasReset)

    const shouldRunInitialFetch = isLoadOnOpen && !didInitialFetch
    const {
        data: options = [],
        isError,
        isFetched,
        isLoading,
    } = useApiQuery(fetchBillReceiversMS, {
        queryName: ConfigQuery.MEETING_SERVICE_BILL_RECEIVERS,
        dependencies: {
            userId: user.userId,
            search: shouldRunInitialFetch ? "" : debouncedSearch,
            forceRefreshKey,
        },
        select: selectBillReceivers,
        enabled: enabled || shouldRunInitialFetch,
    })

    useEffect(() => {
        setDidInitialFetch(false)
    }, [user.userId])

    useEffect(() => {
        if (shouldRunInitialFetch && isFetched) {
            setDidInitialFetch(true)
        }
    }, [isFetched, shouldRunInitialFetch])

    useEffect(() => {
        if (wasReset) {
            didAutoSelectSingleOptionRef.current = false
        }
    }, [wasReset])

    useEffect(() => {
        const singleOption = options.length === 1 ? options[0] : null

        if (!didAutoSelectSingleOptionRef.current && singleOption && !field.value) {
            helpers.setValue(singleOption, false)
            didAutoSelectSingleOptionRef.current = true
        }
    }, [options, field.value, helpers])

    const handleOpenCreateBill = (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsOpenCreateBill(true)
    }

    const ListboxWithHeader = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
        function ListboxWithHeader(props, ref) {
            const { children, ...other } = props
            return (
                <ul ref={ref} {...other}>
                    {showCreateButton && (
                        <li role="presentation" onMouseDown={(e) => e.preventDefault()} className={classes.buttonWrap}>
                            <AddBillReceiverButton onClick={handleOpenCreateBill} className={classes.button} />
                        </li>
                    )}
                    {children}
                </ul>
            )
        },
    )

    const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: BillReceiversItemMS) => {
        const idx = Number((props as any)["data-option-index"] ?? 0)
        const altClass = idx % 2 === 0 ? classes.altWhite : classes.altGrey

        return <BillReceiverOptionItem {...props} className={`${props.className ?? ""} ${altClass}`} option={option} />
    }

    const handleChange = (_e: React.SyntheticEvent, value: BillReceiversItemMS | null) => {
        helpers.setValue(value ?? null)
        if (isLoadOnOpen) {
            handleClearSearch()
        }
    }
    const handleCloseDialog = () => {
        setIsOpenCreateBill(false)
    }

    const noOptionsText = showCreateButton ? (
        <div>
            <div className={classes.noOptionsWrap} onMouseDown={(e) => e.preventDefault()}>
                <AddBillReceiverButton onClick={handleOpenCreateBill} className={classes.button} />
            </div>
            <LocalizedStrict id="create-order-bill-receiver-autocomplete-no-options">
                <div className={classes.noOptionsTextWrap}>No options</div>
            </LocalizedStrict>
        </div>
    ) : undefined

    return (
        <>
            <Tooltip title={field.value?.name ?? ""} disableHoverListener={!field.value || open}>
                <ApiQueryAutocomplete
                    isLoading={enabled && isLoading}
                    isError={isError}
                    options={options}
                    getOptionLabel={(option: BillReceiversItemMS) => option.name || ""}
                    value={field.value || null}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    size="small"
                    noOptionsText={noOptionsText}
                    slotProps={{
                        textField: formikErrorProps,
                        popper: {
                            placement: "bottom-end",
                            sx: {
                                width: "40rem !important",
                                "& .MuiAutocomplete-listbox": {
                                    padding: 0,
                                },
                                "& .MuiAutocomplete-noOptions": {
                                    padding: 0,
                                },
                            },
                        },
                    }}
                    label={`${config?.label || config?.description}${config?.inputField?.isRequired ? " *" : ""}`}
                    isOptionEqualToValue={(a: BillReceiversItemMS, b: BillReceiversItemMS) => a.id === b.id}
                    filterOptions={(x) => x}
                    readOnly={isEagerLoad && options.length === 1}
                    renderOption={renderOption}
                    ListboxComponent={showCreateButton ? ListboxWithHeader : undefined}
                />
            </Tooltip>
            {isOpenCreateBill && <CreateBillReceiver userId={user.userId} handleCloseDialog={handleCloseDialog} />}
        </>
    )
}
